import type { EditFieldGovernance, EditPath } from "../types";

export interface PathPresentation {
  chipLabel: string;
  chipBg: string;
  chipFg: string;
  primaryActionLabel: string;
  explanation: string;
}

const PRESENTATION: Record<EditPath, PathPresentation> = {
  A: {
    chipLabel: "Applies immediately",
    chipBg: "#E7F5EC",
    chipFg: "#0F5132",
    primaryActionLabel: "Save change",
    explanation: "This change is validated and saved right away.",
  },
  B: {
    chipLabel: "Needs verification",
    chipBg: "#FFF3D6",
    chipFg: "#B45309",
    primaryActionLabel: "Send verification code",
    explanation:
      "We'll send a one-time code to confirm this change before it is saved.",
  },
  C: {
    chipLabel: "Needs approval",
    chipBg: "#E4E9FE",
    chipFg: "#2B44C1",
    primaryActionLabel: "Send for approval",
    explanation:
      "This change is sent for approval. Once approved it is applied to your profile and connected systems.",
  },
  BLOCKED: {
    chipLabel: "Blocked",
    chipBg: "#FADBD8",
    chipFg: "#B31412",
    primaryActionLabel: "Blocked from edit",
    explanation:
      "This field can't be changed through the profile flow. Reach out to your Vendor Manager for the exception path.",
  },
  SYSTEM: {
    chipLabel: "System-managed",
    chipBg: "#F0F1F5",
    chipFg: "#4A5568",
    primaryActionLabel: "System-managed",
    explanation:
      "This value is derived by the system from other fields and can't be edited directly.",
  },
};

const UNKNOWN_PRESENTATION: PathPresentation = {
  chipLabel: "Unavailable",
  chipBg: "#F0F1F5",
  chipFg: "#4A5568",
  primaryActionLabel: "",
  explanation:
    "This field's governance couldn't be read. Please try again later.",
};

export function isKnownPath(path: unknown): path is EditPath {
  return (
    path === "A" ||
    path === "B" ||
    path === "C" ||
    path === "BLOCKED" ||
    path === "SYSTEM"
  );
}

export function presentationFor(path: EditPath | string | undefined): PathPresentation {
  if (isKnownPath(path)) return PRESENTATION[path];
  return UNKNOWN_PRESENTATION;
}

export const VENDOR_INITIATABLE_FIELDS = new Set<string>([
  "email_id",
  "po_notification_emails",
  "escalation_emails",
  "vendor_invoice_portal_access_email",
  "tars_production_process_management_access_email",
  "address_billing",
  "location_pincode",
  "account_number",
  "ifsc_code",
  "poc_contact",
  "msme_certificate",
]);

// GST intentionally excluded — locked here for VM too (BE ERR-GST-001).
// legal_name intentionally excluded — PAN-derived; VM edits PAN to re-fetch
// (see buildPanNumberEditTarget + EditDrawer isPanLegalName). QET-650.
const VM_LOCKED_BANK_FIELDS = new Set<string>(["account_number", "ifsc_code"]);

export const VM_INITIATABLE_FIELDS = new Set<string>([
  ...[...VENDOR_INITIATABLE_FIELDS].filter((f) => !VM_LOCKED_BANK_FIELDS.has(f)),
  "vendor_type",
  "super_category",
  "sub_category",
  "credit_days",
  "payment_terms_pattern",
  "pan_number",
]);

const PAN_LOCKED_STATUS = "VALID";
function panEditGatedByStatus(rawStatus: string | null | undefined): boolean {
  const status = String(rawStatus ?? "").trim().toUpperCase();
  return status === PAN_LOCKED_STATUS;
}

const FRIENDLY_LOCK_COPY: Record<string, string> = {
  pan_number:
    "PAN can't be changed here. Please contact your Vendor Manager to update it.",
  legal_name:
    "Business name can't be changed here. Please contact your Vendor Manager to update it.",
  vendor_type:
    "Vendor Type is set at onboarding. Please contact your Vendor Manager to update it.",
  super_category:
    "Vendor Profile Type is set by your Vendor Manager. Please reach out to them to update it.",
  sub_category:
    "Vendor Profile Type is set by your Vendor Manager. Please reach out to them to update it.",
  gst:
    "GST once added can't be changed here. Please contact your Vendor Manager if this needs correcting.",
  credit_days:
    "Credit Days is set by your Vendor Manager. Please contact them to update it.",
  payment_terms_pattern:
    "Payment Terms Pattern is set by your Vendor Manager. Please contact them to update it.",
  payment_terms_remarks:
    "Auto-generated from your commercial terms.",
};

// Fields whose vmLockCopy describes a permanent invariant (not a conditional
// unlock). For these, the design-intent copy always wins over governance-state
// copy. See lockTooltip for the rationale.
const VM_DERIVATION_FIELDS = new Set([
  "legal_name",
  "gst",
  "payment_terms_remarks",
  "account_number",
  "ifsc_code",
]);

function vmLockCopy(fieldName: string, ctx: RowLockContext): string | null {
  switch (fieldName) {
    case "pan_number": {
      const status = String(ctx.panStatus ?? "").trim();
      const suffix = status
        ? ` Current PAN status: ${status}.`
        : " No PAN status recorded yet.";
      return (
        "PAN can only be re-entered when its verification status isn't VALID (e.g. INVALID or VERIFICATION_FAILED)." +
        suffix
      );
    }
    case "vendor_type":
      return "Only the Non-GST → GST transition is allowed here. Other vendor-type changes go through Compliance offline.";
    case "gst":
      return "GST cannot be edited here. Once set, it's fixed — corrections go through the Compliance workflow (ERR-GST-001).";
    case "legal_name":
      return "Legal Name is derived from PAN. Update PAN (once its verification status isn't VALID) to trigger a re-fetch.";
    case "payment_terms_remarks":
      return "Auto-generated from Credit Days + Payment Terms Pattern.";
    case "account_number":
    case "ifsc_code":
      return "Bank details can only be changed by the vendor.";
    default:
      return null;
  }
}

export function lockTooltip(
  gov: EditFieldGovernance | undefined,
  fieldName: string,
  ctx: RowLockContext & { isInternal?: boolean } = {},
): string {
  if (ctx.isInternal) {
    if (gov?.path === "SYSTEM") return "Auto-managed by the system.";
    // Derivation-copy fields describe a permanent invariant (legal_name is
    // PAN-derived; payment_terms_remarks is computed; gst is one-shot-set). The
    // vmLockCopy is the correct message regardless of what governance says.
    // Conditional-unlock fields (pan_number, vendor_type) describe *when* an
    // edit is permitted, so if governance blocks the field outright we must
    // fall through to the generic governance-state copy — otherwise we'd tell
    // the VM they can edit under condition X while BE has actually disallowed
    // it. QET-650 review round 2.
    if (VM_DERIVATION_FIELDS.has(fieldName)) {
      const vm = vmLockCopy(fieldName, ctx);
      if (vm) return vm;
    }
    if (gov && gov.editable === false) {
      return "Governance says this field isn't editable from this surface.";
    }
    const vm = vmLockCopy(fieldName, ctx);
    if (vm) return vm;
    return "Governance entry not yet published for this field. Coming soon.";
  }
  const friendly = FRIENDLY_LOCK_COPY[fieldName];
  if (friendly) return friendly;
  if (gov?.path === "SYSTEM") return "Auto-managed by the system.";
  return "Not editable from here. Please contact your Vendor Manager for help.";
}

export interface RowLockContext {
  panStatus?: string | null;
}

export function isRowLocked(
  gov: EditFieldGovernance | undefined,
  currentValue: unknown,
  fieldName: string,
  isInternal = false,
  ctx: RowLockContext = {},
): boolean {
  const whitelist = isInternal
    ? VM_INITIATABLE_FIELDS
    : VENDOR_INITIATABLE_FIELDS;
  if (!whitelist.has(fieldName)) return true;
  if (!gov) return true;
  if (!gov.editable) return true;
  if (fieldName === "vendor_type") {
    const v = typeof currentValue === "string" ? currentValue.trim() : "";
    if (v !== "Non-GST") return true;
  }
  if (fieldName === "pan_number") {
    if (panEditGatedByStatus(ctx.panStatus)) return true;
  }
  return false;
}
